// =========================================================================
// HelpyDoc Metabolic · Firebase Service
// Imports estáticos → Firebase se bundlea correctamente en producción.
// Configuración con fallback hardcodeado → funciona sin secrets en CI.
// =========================================================================

import { Capacitor } from '@capacitor/core';
import { _setFirebaseUser } from '@/services/AuthService';
import type { AuthResult, Session } from '@/services/AuthService';
import { initializeApp, getApps } from 'firebase/app';
import * as FirebaseAuthNS from 'firebase/auth';
import {
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  signOut,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  getAdditionalUserInfo,
} from 'firebase/auth';
import type { Auth } from 'firebase/auth';
import type { FirebaseApp } from 'firebase/app';

// `initializeAuth` e `indexedDBLocalPersistence` se leen del espacio de nombres
// en vez de por import nombrado A PROPÓSITO: en este árbol de dependencias el
// paquete `@firebase/auth` no está desglosado, así que sus `.d.ts` no exponen
// esos dos símbolos aunque el runtime SÍ los exporta (comprobado con
// `import * as a from 'firebase/auth'` → ambos presentes). Con el import
// nombrado, `tsc -b` aborta la build y ni siquiera se llega a empaquetar.
type _InitAuth = (app: FirebaseApp, deps?: { persistence?: unknown }) => Auth;
const _initializeAuth = (FirebaseAuthNS as unknown as { initializeAuth?: _InitAuth }).initializeAuth;
const _idbPersistence = (FirebaseAuthNS as unknown as { indexedDBLocalPersistence?: unknown }).indexedDBLocalPersistence;

// ── Configuración Firebase (solo vars de entorno, sin fallback hardcodeado) ──
// Las credenciales del SDK web de Firebase son públicas por diseño (van en el
// bundle de producción); la seguridad la dan las Security Rules.
// La fuente de verdad para producción es .env.production.
// Sin vars configuradas (CI, fork, dev sin .env.local) → Firebase deshabilitado.

/** Firebase habilitado solo si VITE_FIREBASE_API_KEY está configurada. */
export const isFirebaseEnabled = !!import.meta.env.VITE_FIREBASE_API_KEY;

const _cfg = isFirebaseEnabled ? {
  apiKey:        import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain:    import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId:     import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId:         import.meta.env.VITE_FIREBASE_APP_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
} : null;

// ── Inicialización ─────────────────────────────────────────────────────────

let _auth: Auth | null = null;

function _getAuth(): Auth {
  if (_auth) return _auth;
  if (!_cfg) throw new Error('[Firebase] No configurado: faltan VITE_FIREBASE_* env vars');
  // Evitar doble inicialización (HMR en dev)
  const app = getApps().length === 0 ? initializeApp(_cfg) : getApps()[0];

  // ── OJO: en la carcasa nativa NO se puede usar getAuth() ──────────────────
  //
  // `getAuth()` instala el resolver de OAuth por defecto, que monta un iframe
  // contra https://<authDomain>/__/auth/iframe usando gapi. Ese saludo inicial
  // NUNCA se completa cuando el origen es `capacitor://localhost`, porque no es
  // un origen http(s) y la ventana padre no recibe respuesta.
  //
  // El detalle que lo hace difícil de diagnosticar: Firebase deja TODAS las
  // operaciones esperando a `_initializationPromise`. Así que
  // signInWithEmailAndPassword se queda pendiente para siempre, sin lanzar
  // error y sin timeout — en la interfaz se ve como un botón «Entrando…» que
  // no avanza. Verificado en iPhone el 2026-07-27: la app cargaba
  // apis.google.com/js/api.js y el iframe se quedaba sin crear.
  //
  // `initializeAuth` sin `popupRedirectResolver` evita el iframe por completo.
  // Consecuencia asumida: en nativo NO funcionan signInWithPopup (Google/Apple);
  // tampoco funcionaban antes —se colgaban igual— y su solución es un plugin
  // nativo, no este resolver.
  let auth: Auth;
  if (Capacitor.isNativePlatform() && _initializeAuth) {
    try {
      // Con persistencia en IndexedDB para no tener que volver a iniciar
      // sesión en cada arranque de la app.
      auth = _initializeAuth(app, { persistence: _idbPersistence });
    } catch {
      // Ya inicializado (p. ej. dos llamadas concurrentes): reutilizar.
      auth = getAuth(app);
    }
  } else {
    auth = getAuth(app);
  }
  _auth = auth;
  return auth;
}

// Promesa que se resuelve cuando onAuthStateChanged dispara por primera vez.
// Permite esperar a que Firebase hidrate el usuario antes de hacer peticiones API.
// Usamos un objeto para evitar que TypeScript estreche el tipo a `never` en bloques catch.
const _ready = { fn: null as VoidFunction | null };
const _firebaseReady = new Promise<void>(resolve => { _ready.fn = resolve; });

/** Espera a que Firebase Auth esté hidratado (onAuthStateChanged primera llamada). */
export async function waitForFirebaseReady(timeoutMs = 3000): Promise<void> {
  if (!isFirebaseEnabled) return; // sin Firebase, no hay nada que esperar
  return Promise.race([
    _firebaseReady,
    new Promise<void>(resolve => setTimeout(resolve, timeoutMs)),
  ]);
}

// Arrancar en background al importar el módulo (solo si Firebase está configurado)
if (isFirebaseEnabled) {
  try {
    const auth = _getAuth();
    onAuthStateChanged(auth, (user) => {
      _setFirebaseUser(user);
      // Resolver la promesa de "listo" (solo la primera vez)
      _ready.fn?.(); _ready.fn = null;
      // Si hay usuario activo y no hay sesión guardada, restaurarla (login persistido)
      if (user) {
        const existing = localStorage.getItem('helpydoc_session');
        if (!existing) {
          const session: Session = {
            userId: user.uid,
            email:  user.email ?? '',
            nombre: user.displayName ?? (user.email?.split('@')[0] ?? 'Usuario'),
            token:  user.uid,
            createdAt: new Date().toISOString(),
          };
          localStorage.setItem('helpydoc_session', JSON.stringify(session));
        }
      }
    });
  } catch (err) {
    console.warn('[Firebase] Error al inicializar:', err);
    // En caso de error de inicialización, resolver inmediatamente para no bloquear la app
    _ready.fn?.(); _ready.fn = null;
  }
}

// ── Sign In ───────────────────────────────────────────────────────────────

/**
 * Inicia sesión con Firebase Auth.
 * Guarda la sesión en localStorage y registra el usuario en AuthService.
 */
export async function firebaseSignIn(email: string, password: string): Promise<AuthResult> {
  try {
    const auth = _getAuth();
    const cred = await signInWithEmailAndPassword(auth, email, password);
    const user = cred.user;
    const session = _buildSession(user);
    localStorage.setItem('helpydoc_session', JSON.stringify(session));
    _setFirebaseUser(user);
    return { ok: true, session };
  } catch (err: unknown) {
    return { ok: false, error: _friendlyError(err) };
  }
}

// ── Sign In con Google ──────────────────────────────────────────────────────

/**
 * Inicia sesión con Google (popup OAuth). Crea la cuenta automáticamente la
 * primera vez. Requiere el proveedor Google habilitado en Firebase Auth.
 */
export async function firebaseSignInWithGoogle(): Promise<AuthResult> {
  try {
    const auth = _getAuth();
    const cred = await signInWithPopup(auth, new GoogleAuthProvider());
    const user = cred.user;
    const isNew = getAdditionalUserInfo(cred)?.isNewUser ?? false;
    const session: Session = { ..._buildSession(user), isNew };
    localStorage.setItem('helpydoc_session', JSON.stringify(session));
    _setFirebaseUser(user);
    return { ok: true, session };
  } catch (err: unknown) {
    return { ok: false, error: _friendlyError(err, 'Google') };
  }
}

/** Inicia sesión con Apple. Requiere el proveedor Apple habilitado en Firebase. */
export async function firebaseSignInWithApple(): Promise<AuthResult> {
  try {
    const auth = _getAuth();
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    const cred = await signInWithPopup(auth, provider);
    const user = cred.user;
    const isNew = getAdditionalUserInfo(cred)?.isNewUser ?? false;
    const session: Session = { ..._buildSession(user), isNew };
    localStorage.setItem('helpydoc_session', JSON.stringify(session));
    _setFirebaseUser(user);
    return { ok: true, session };
  } catch (err: unknown) {
    return { ok: false, error: _friendlyError(err, 'Apple') };
  }
}

// ── Sign Up ───────────────────────────────────────────────────────────────

/**
 * Crea una cuenta con Firebase Auth.
 * Actualiza el displayName y guarda la sesión.
 */
export async function firebaseSignUp(
  email: string,
  password: string,
  nombre: string,
): Promise<AuthResult> {
  try {
    const auth = _getAuth();
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(cred.user, { displayName: nombre });
    const user = cred.user;
    const session: Session = {
      ..._buildSession(user),
      nombre,
      isNew: true,
    };
    localStorage.setItem('helpydoc_session', JSON.stringify(session));
    _setFirebaseUser(user);
    return { ok: true, session };
  } catch (err: unknown) {
    return { ok: false, error: _friendlyError(err) };
  }
}

// ── Sign Out ──────────────────────────────────────────────────────────────

/** Cierra sesión de Firebase y limpia el estado local. */
export async function firebaseSignOut(): Promise<void> {
  try {
    const auth = _getAuth();
    await signOut(auth);
    _setFirebaseUser(null);
  } catch (err) {
    console.warn('[Firebase] Error al cerrar sesión:', err);
  }
}

// ── Helpers privados ──────────────────────────────────────────────────────

function _buildSession(user: { uid: string; email: string | null; displayName: string | null }): Session {
  return {
    userId: user.uid,
    email:  user.email ?? '',
    nombre: user.displayName ?? (user.email?.split('@')[0] ?? 'Usuario'),
    token:  user.uid,
    createdAt: new Date().toISOString(),
  };
}

/**
 * Traduce el error de Firebase a algo legible.
 *
 * `proveedor` NO es decorativo: dos de estos mensajes nombran al proveedor, y
 * estaban fijos en "Google". Quien pulsaba «Continuar con Apple» y no tenía el
 * proveedor activado leía "El acceso con Google no está activado", que manda a
 * buscar el problema donde no está.
 */
function _friendlyError(err: unknown, proveedor = 'este método de acceso'): string {
  const msg = (err as { message?: string })?.message ?? '';
  if (msg.includes('invalid-credential') || msg.includes('user-not-found') || msg.includes('wrong-password'))
    return 'Email o contraseña incorrectos';
  if (msg.includes('email-already-in-use'))  return 'Este email ya está registrado';
  if (msg.includes('weak-password'))         return 'La contraseña debe tener al menos 6 caracteres';
  if (msg.includes('invalid-email'))         return 'Email no válido';
  if (msg.includes('too-many-requests'))     return 'Demasiados intentos. Inténtalo más tarde.';
  if (msg.includes('network-request-failed')) return 'Sin conexión. Revisa tu internet.';
  if (msg.includes('popup-closed-by-user') || msg.includes('cancelled-popup-request'))
    return 'Inicio de sesión cancelado';
  if (msg.includes('popup-blocked'))
    return `El navegador bloqueó la ventana de ${proveedor}. Permite las ventanas emergentes.`;
  if (msg.includes('account-exists-with-different-credential'))
    return 'Ya tienes una cuenta con ese email usando otro método de acceso';
  if (msg.includes('operation-not-allowed')) return `El acceso con ${proveedor} no está activado en el servidor`;
  return msg || 'Error de autenticación';
}
